import { Alert, Anchor, Badge, Button, Fieldset, Group, Table, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconChevronLeft, IconChevronRight, IconPhoneEnd } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { motion } from "motion/react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { senderQueryOptions } from "shared/graphql/senders";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { arr } from "shared/utils/array";
import { fail, to, voiding } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";

const Page = () => {
    const customerPhoneNumbers = useSuspenseQuery(senderQueryOptions.phoneNumbers).data.getAvailablePhoneNumbers;
    const systemwidePhoneNumbers = useSuspenseQuery(queries.allPhoneNumbers).data.phoneNumbers;
    const nav = useNavigate();

    const save = useGraphqlMutation(
        graphql(`
            mutation DevtoolsSaveCustomerPhoneNumbers($phoneNumbers: [String!]!) {
                support {
                    setCustomerPhoneNumbers(phoneNumbers: $phoneNumbers) {
                        phoneNumbers {
                            phoneNumber
                        }
                    }
                }
            }
        `),
    );

    const form = useForm({
        initialValues: {
            unbound: systemwidePhoneNumbers.filter((it) => !customerPhoneNumbers.includes(it.phoneNumber)),
            bound: customerPhoneNumbers.map(
                (each) => systemwidePhoneNumbers.find((it) => it.phoneNumber == each) ?? fail("unknown: " + each),
            ),
        },
    });

    return (
        <Content paper>
            <Content.Heading backable>Customer Phone Numbers</Content.Heading>

            <Group align="flex-start">
                <Fieldset legend="Systemwide" flex={1}>
                    {form.values.unbound.length == 0 && (
                        <Alert color="gray" title="No remaining systemwide phone numbers" icon={<IconPhoneEnd size={16} />}>
                            <Text size="sm">
                                To add more, navigate to the{" "}
                                <Anchor
                                    c="var(--mantine-color-text)"
                                    td="underline"
                                    component={Link}
                                    to="/devtools/systemwide-phonenumbers"
                                >
                                    Systemwide Phone Number page.
                                </Anchor>
                            </Text>
                        </Alert>
                    )}
                    <Table striped>
                        <Table.Tbody>
                            {form.values.unbound.map((each) => (
                                <motion.tr key={each.phoneNumber} layout layoutId={each.phoneNumber}>
                                    <Table.Td>{each.name}</Table.Td>
                                    <Table.Td>
                                        <Badge variant="default" size="lg" styles={{ label: { minWidth: "fit-content" } }}>
                                            {each.phoneNumber}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Button
                                            variant="default"
                                            size="xs"
                                            rightSection={<IconChevronRight size={16} />}
                                            onClick={() => {
                                                form.setFieldValue("bound", (it) => arr(it).append(each).take());
                                                form.setFieldValue("unbound", (it) =>
                                                    arr(it)
                                                        .eqBy((a, b) => a.phoneNumber == b.phoneNumber)
                                                        .except(each)
                                                        .take(),
                                                );
                                            }}
                                        >
                                            Add to Customer
                                        </Button>
                                    </Table.Td>
                                </motion.tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Fieldset>
                <Fieldset legend="Customer" flex={1}>
                    {form.values.bound.length == 0 && (
                        <Alert
                            variant="light"
                            icon={<IconPhoneEnd size={16} />}
                            color="gray"
                            c="gray"
                            title="No phone numbers added to this customer."
                        >
                            You can add systemwide phone numbers to this customers. Phone numbers may be added to any number of customers.
                        </Alert>
                    )}
                    {form.values.bound.length > 0 && (
                        <Table flex={1}>
                            <Table.Tbody>
                                {form.values.bound.map((each) => (
                                    <motion.tr key={each.phoneNumber} layout layoutId={each.phoneNumber}>
                                        <Table.Td>
                                            <Button
                                                variant="default"
                                                size="xs"
                                                leftSection={<IconChevronLeft size={16} />}
                                                onClick={() => {
                                                    form.setFieldValue("unbound", (it) => arr(it).append(each).take());
                                                    form.setFieldValue("bound", (it) =>
                                                        arr(it)
                                                            .eqBy((a, b) => a.phoneNumber == b.phoneNumber)
                                                            .except(each)
                                                            .take(),
                                                    );
                                                }}
                                            >
                                                Remove from Customer
                                            </Button>
                                        </Table.Td>
                                        <Table.Td>{each.name}</Table.Td>
                                        <Table.Td>
                                            <Badge variant="default" size="lg" styles={{ label: { minWidth: "fit-content" } }}>
                                                {each.phoneNumber}
                                            </Badge>
                                        </Table.Td>
                                    </motion.tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    )}
                </Fieldset>
            </Group>

            <Group justify="flex-end">
                <Button
                    disabled={!form.isDirty()}
                    onClick={voiding(async () => {
                        const { close } = await notify.confirm("Are you sure you want to save this configuration?");
                        await save.mutateAsync({ phoneNumbers: form.getValues().bound.map(to("phoneNumber")) });
                        notify.show.success("Saved");
                        close();
                        await qk.invalidate("senders", "phoneNumbers");
                        await nav({ to: "/devtools/customer" });
                    })}
                >
                    Save Configuration
                </Button>
            </Group>
        </Content>
    );
};

const queries = {
    allPhoneNumbers: queryOptions({
        queryKey: qk("support", "phone-numbers"),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query SupportPhoneNumbers {
                        support {
                            phoneNumbers {
                                phoneNumber
                                name
                            }
                        }
                    }
                `),
            ),

        select: to("support"),
    }),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/customer/numbers")({
    component: Page,
});
